import { Link, Outlet, useLocation, useMatches, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNewOrderNotifications } from '@/hooks/useNewOrderNotifications';
import { useClubFeatureFlags } from '@/hooks/useClubFeatureFlags';
import { useInternalNavCounts, type InternalNavCounts } from '@/hooks/useInternalNavCounts';
import { getTenantNav, getKioskNav, getVenueNav } from '@/lib/clubNav';
import { workspaceKindLabel, workspaceHome, workspacePortalLabel } from '@/lib/workspaces';
import { cn } from '@/lib/utils';
import {
  Building2,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
} from
  'lucide-react';
import ThemeToggle from './ThemeToggle';
import { CommandPalette } from './CommandPalette';
import { TranslationActivityLine } from './TranslationActivityLine';
import { ViewAsBanner } from './ViewAsBanner';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';
import { captureCurrentTab } from '@/lib/screenCapture';
import { Bug as BugIcon } from 'lucide-react';
import idseatLogo from '@/assets/idseat-logo.svg';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFanBaseUrl, generateFanPageUrl } from '@/lib/hostname';
import { supabase } from '@/integrations/supabase/client';
import { ExternalLink } from 'lucide-react';
import { ContentSkeleton } from './ContentSkeleton';
import { InternalAdminMfaGate } from './InternalAdminMfaGate';
import { PageTitleContext, useShellRendered, type PageTitleContextValue } from './PageTitleContext';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  breadcrumbs?: { label: string; href?: string; }[];
}

/**
 * Shape of the `handle` a route declares so `AdminLayoutRoute` can derive the
 * top-bar title and breadcrumbs from the deepest matched route (see App.tsx).
 */
export interface AdminRouteHandle {
  title: string;
  breadcrumbs?: { label: string; href?: string; }[];
}

/** Derive 1-2 uppercase initials from a club name (e.g. "FC Nordstjärnan" -> "FN"). */
/** The signed-in person's role in the active workspace, as the account row shows it. */
const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', staff: 'Staff' };

function clubInitials(name?: string | null): string {
  if (!name) return 'C';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'C';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Derive 1-2 uppercase initials from an email/name for the account avatar. */
function accountInitials(email?: string | null): string {
  if (!email) return 'U';
  const local = email.split('@')[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

// Nav icons ported 1:1 from the approved ops-console mockup so the sidebar
// matches it exactly (lucide's set doesn't include a stadium/oval glyph).
type NavIconProps = { className?: string; strokeWidth?: number };
const svgProps = (className?: string, strokeWidth?: number) => ({
  className,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: strokeWidth ?? 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});
const IconDashboard = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><path d="M3 12l9-9 9 9" /><path d="M5 10v10h14V10" /></svg>
);
const IconClubs = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><path d="M3 21h18M6 21V7l6-4 6 4v14" /><path d="M10 21v-5h4v5" /></svg>
);
const IconStadiums = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><ellipse cx="12" cy="12" rx="9" ry="5.5" /><path d="M3 12c0 3 4 5 9 5s9-2 9-5" /></svg>
);
const IconKiosks = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><path d="M3 9l1.5-5h15L21 9" /><path d="M3 9h18v2.5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z" /><path d="M5 14v7h14v-7" /><path d="M10 21v-4h4v4" /></svg>
);
const IconWorkspaces = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
);
const IconEvents = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>
);
const IconLanguages = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" /></svg>
);
const IconTranslations = ({ className, strokeWidth }: NavIconProps) => (
  <svg {...svgProps(className, strokeWidth)}><path d="M4 5h7M8 4v3c0 4-2 7-5 8" /><path d="M6 9c0 3 3 5 6 6" /><path d="M12 20l4-9 4 9M13.5 17h5" /></svg>
);

/**
 * The actual admin shell — sidebar, top bar, and all the global side effects
 * (⌘K palette, club-font toggle, new-order notifications). Kept internal so the
 * exported `AdminLayout` can guard against double-rendering it (see below).
 */
function AdminShell({ children, title, breadcrumbs }: AdminLayoutProps) {
  const { user, signOut, isIdseatAdmin, isClubAdmin, clubName, clubId, workspaces, activeWorkspace, switchWorkspace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // "Report a bug / idea". We capture the screenshot BEFORE opening the modal
  // — the native Screen Capture API grabs whatever the tab is painting, so if
  // the modal were already open it would end up in the shot (which defeats the
  // purpose). Capturing on the click, while the page is still clean, gives a
  // screenshot of what the user was actually looking at. (The old html2canvas
  // path couldn't render this app's layout at all.)
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackShot, setFeedbackShot] = useState<string | null>(null);

  const handleOpenFeedback = async () => {
    const shot = await captureCurrentTab();
    setFeedbackShot(shot);
    setFeedbackOpen(true);
  };

  // Global new-order notifications across the portal: a kiosk workspace
  // chimes for its own orders; a tenant only for a kiosk its organization
  // runs (a tenant served by the venue's caterer has no queue to be called
  // to). The hook keys its subscription on the kiosk id, so a fresh object
  // per render is fine.
  useNewOrderNotifications(
    activeWorkspace?.kind === 'operator'
      ? { operatorId: activeWorkspace.id, ordersPath: '/kiosk' }
      : activeWorkspace?.kind === 'tenant' && activeWorkspace.has_own_operator && activeWorkspace.served_by_operator_id
        ? { operatorId: activeWorkspace.served_by_operator_id, ordersPath: '/club/orders' }
        : null,
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Determine which portal this is: the operations console (/admin), the
  // Kiosk Portal (/kiosk), the Stadium Portal (/venue) or the Tenant Portal
  // (everything else under the shell). The three share the same chrome; `isPortal` is any of them.
  const isInternalAdmin = location.pathname.startsWith('/admin');
  const isKiosk = location.pathname.startsWith('/kiosk');
  const isVenue = location.pathname.startsWith('/venue');
  const isClub = !isInternalAdmin && !isKiosk && !isVenue;
  const isPortal = !isInternalAdmin;
  const portalName = isKiosk
    ? (activeWorkspace?.name || 'Kiosk')
    : isVenue
      ? (activeWorkspace?.name || 'Stadium')
      : (clubName || 'Club');
  const portalLabel = isInternalAdmin
    ? 'Operations console'
    : workspacePortalLabel(isKiosk ? 'operator' : isVenue ? 'venue' : 'tenant');

  // The tenant and kiosk portals render in Poppins (stakeholder request).
  // Toggled on <body> so Radix portals (dialogs, dropdowns, sheets) inherit it
  // too; internal admin and the fan page keep Inter/Outfit. See
  // body.club-font in index.css.
  useEffect(() => {
    document.body.classList.toggle('club-font', isPortal);
    return () => document.body.classList.remove('club-font');
  }, [isPortal]);

  // Grouped internal nav — labelled sections with per-item count badges (Feature 1).
  const internalNavGroups: {
    label: string;
    items: { label: string; href: string; icon: React.ComponentType<NavIconProps>; countKey?: keyof InternalNavCounts }[];
  }[] = [
    {
      label: 'Overview',
      items: [
        { label: 'Dashboard', href: '/admin', icon: IconDashboard },
      ],
    },
    {
      label: 'Manage',
      items: [
        { label: 'Clubs', href: '/admin/clubs', icon: IconClubs, countKey: 'clubs' },
        { label: 'Stadiums', href: '/admin/stadiums', icon: IconStadiums, countKey: 'stadiums' },
        { label: 'Kiosks', href: '/admin/kiosks', icon: IconKiosks, countKey: 'kiosks' },
        { label: 'Workspaces', href: '/admin/workspaces', icon: IconWorkspaces, countKey: 'workspaces' },
        { label: 'Events', href: '/admin/events', icon: IconEvents, countKey: 'events' },
        { label: 'Live polls', href: '/admin/live-polls', icon: IconEvents },
      ],
    },
    {
      label: 'Localization',
      items: [
        { label: 'Languages', href: '/admin/languages', icon: IconLanguages, countKey: 'languages' },
        { label: 'Translations', href: '/admin/translations', icon: IconTranslations },
      ],
    },
  ];

  const { data: internalNavCounts } = useInternalNavCounts(isInternalAdmin);

  // Global ⌘K / Ctrl+K shortcut to open the command palette (internal admin only).
  useEffect(() => {
    if (!isInternalAdmin) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInternalAdmin]);

  const { flags } = useClubFeatureFlags(isInternalAdmin ? null : clubId);
  const portalNavItems = isKiosk ? getKioskNav(activeWorkspace?.role) : isVenue ? getVenueNav() : getTenantNav(flags, activeWorkspace);

  // Resolve the club's real fan-page URL from its stadium slug (mirrors ClubDesign.tsx ~lines 104–114).
  // Cached across remounts (react-query) so it doesn't refetch on every route change.
  const { data: stadiumSlug } = useQuery({
    queryKey: ['club-stadium-slug', clubId],
    staleTime: Infinity,
    enabled: !isInternalAdmin && !!clubId,
    queryFn: async () => {
      const { data } = await supabase
        .from('stadium_clubs')
        .select('stadium_id, stadiums!inner(slug)')
        .eq('club_id', clubId!)
        .limit(1)
        .maybeSingle();
      const slug = (data?.stadiums as any)?.slug as string | undefined;
      return slug ?? null;
    },
  });

  // Build the fan-page href: use the real club URL once slug is known, else fall back to base URL.
  // Always append ?club_id= so TagPage can resolve the preview without a real NFC tag scan.
  const fanPageHref = stadiumSlug && clubId
    ? `${generateFanPageUrl(stadiumSlug, 'PREVIEW')}?club_id=${clubId}`
    : getFanBaseUrl();

  // Club crest for the sidebar brand tile. Clubs have no logo column, so use
  // the same derivation as the internal Clubs table: the most recent event's
  // home_team_logo_url, else the SportMonks CDN crest from the team id, else
  // fall back to initials (also on image load error).
  // Cached across remounts (react-query, persisted to localStorage) so the crest
  // doesn't refetch — and flash initials — on every route change.
  const { data: clubLogoUrl } = useQuery({
    queryKey: ['club-brand-logo', clubId],
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24, // 24h
    enabled: !isInternalAdmin && !!clubId,
    queryFn: async () => {
      const { data: ev } = await supabase
        .from('events')
        .select('home_team_logo_url')
        .eq('club_id', clubId!)
        .not('home_team_logo_url', 'is', null)
        .order('kickoff_datetime', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (ev?.home_team_logo_url) return ev.home_team_logo_url as string;
      const { data: club } = await supabase
        .from('clubs')
        .select('sportsmonks_team_id')
        .eq('id', clubId!)
        .maybeSingle();
      const teamId = (club as any)?.sportsmonks_team_id as number | null | undefined;
      return teamId
        ? `https://cdn.sportmonks.com/images/soccer/teams/${teamId % 32}/${teamId}.png`
        : null;
    },
  });
  // Keep the img-onError fallback to initials; reset when the resolved URL changes.
  const [logoBroken, setLogoBroken] = useState(false);
  useEffect(() => { setLogoBroken(false); }, [clubLogoUrl]);

  // ── UNIFIED ADMIN SHELL — the light club-portal design serves both the club
  // admin and the internal (IDSeat staff) admin. Brand, nav, footer, breadcrumb
  // and top-bar actions vary by `isClub`; the chrome is identical so the two
  // portals feel like one product. (Port of docs/design/club-portal-mockup.dc.html.)
  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen &&
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)} />
      }

      {/* Sidebar — mockup .sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-[252px] flex-shrink-0 bg-card border-r border-border",
        "flex flex-col px-[14px] py-[18px] gap-[3px] h-full overflow-y-auto",
        "lg:sticky lg:top-0 lg:translate-x-0 transition-transform",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Brand row — mockup .brandrow */}
        <div className="flex items-center gap-[11px] px-2 pt-1.5 pb-4">
          {isClub && clubLogoUrl && !logoBroken ? (
            <div className="w-10 h-10 rounded-[12px] bg-white border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={clubLogoUrl}
                alt=""
                className="w-[82%] h-[82%] object-contain"
                onError={() => setLogoBroken(true)}
              />
            </div>
          ) : isPortal ? (
            <div className="w-10 h-10 rounded-[12px] bg-primary text-primary-foreground flex items-center justify-center font-extrabold text-base flex-shrink-0">
              {clubInitials(portalName)}
            </div>
          ) : (
            /* IDSeat brand mark — the SVG carries its own white background. */
            <div className="w-10 h-10 rounded-[12px] border border-border overflow-hidden flex-shrink-0">
              <img src={idseatLogo} alt="IDSeat" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-extrabold text-[15px] leading-tight tracking-tight text-foreground truncate">
              {isPortal ? portalName : 'IDSeat'}
            </div>
            <div className="text-sm font-semibold text-muted-foreground">
              {portalLabel}
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden ml-auto text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search platform — opens the ⌘K command palette (internal only) */}
        {isInternalAdmin && (
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2.5 bg-surface border border-border rounded-[10px] px-3 py-2.5 mb-2 text-[13.5px] font-medium text-muted-foreground hover:bg-muted/50 transition-colors">
            <Search className="w-4 h-4 flex-shrink-0" />
            <span>Search platform…</span>
            <span className="ml-auto text-[11px] font-bold text-muted-foreground/70 border border-border rounded-[6px] px-1.5 py-0.5">⌘K</span>
          </button>
        )}

        {/* Nav — mockup .navitem */}
        <nav className="flex flex-col gap-[3px]">
          {isInternalAdmin ? (
            internalNavGroups.map((group) => (
              <div key={group.label} className="flex flex-col gap-[3px]">
                <div className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground/70 px-2.5 pt-3 pb-1">
                  {group.label}
                </div>
                {group.items.map((item) => {
                  const active = location.pathname === item.href;
                  const count = item.countKey ? internalNavCounts?.[item.countKey] : undefined;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-[11px] text-[15px] font-semibold transition-colors",
                        active
                          ? "bg-accent/10 text-accent"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}>
                      <item.icon className="w-[19px] h-[19px] flex-shrink-0" strokeWidth={1.8} />
                      <span>{item.label}</span>
                      {count !== undefined &&
                        <span className={cn(
                          "ml-auto text-xs font-bold tabular-nums",
                          active ? "text-accent" : "text-muted-foreground/70"
                        )}>
                          {count}
                        </span>
                      }
                    </Link>
                  );
                })}
              </div>
            ))
          ) : (
            portalNavItems.map((item) => {
              const active = location.pathname === item.href;
              // A disabled item stays visible so a tenant is never left
              // guessing where Orders went, but it is not a link.
              if (item.disabled) {
                return (
                  <div
                    key={item.href}
                    role="link"
                    aria-disabled="true"
                    title={item.disabledReason}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-[11px] text-[15px] font-semibold text-muted-foreground/50 cursor-not-allowed select-none">
                    <item.icon className="w-[19px] h-[19px] flex-shrink-0" strokeWidth={1.8} />
                    <span>{item.label}</span>
                  </div>
                );
              }
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-[11px] text-[15px] font-semibold transition-colors",
                    active
                      ? "bg-accent/10 text-accent"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}>
                  <item.icon className="w-[19px] h-[19px] flex-shrink-0" strokeWidth={1.8} />
                  <span>{item.label}</span>
                </Link>
              );
            })
          )}
          {/* Cross-links between the operations console and the portals */}
          {isPortal && isIdseatAdmin &&
            <Link
              to="/admin"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-[11px] text-[15px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <LayoutDashboard className="w-[19px] h-[19px] flex-shrink-0" strokeWidth={1.8} />
              <span>Internal Admin</span>
            </Link>
          }
          {isInternalAdmin && isClubAdmin &&
            <Link
              to={activeWorkspace ? workspaceHome(activeWorkspace.kind) : '/club'}
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-[11px] text-[15px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <Building2 className="w-[19px] h-[19px] flex-shrink-0" strokeWidth={1.8} />
              <span>{activeWorkspace ? workspacePortalLabel(activeWorkspace.kind) : 'Tenant Portal'}</span>
            </Link>
          }
        </nav>

        {/* Footer — mockup .sidefoot */}
        <div className="mt-auto flex flex-col gap-2.5 pt-3">
          {/* Your fan page — mockup .livecard (club only) */}
          {isClub &&
            <div className="border border-border rounded-[13px] p-3 flex flex-col gap-2">
              <div className="text-sm font-semibold text-muted-foreground">Your fan page</div>
              <div className="flex items-center gap-2 bg-muted rounded-[9px] px-2.5 py-2 text-sm font-semibold text-muted-foreground">
                <span className="w-[7px] h-[7px] rounded-full bg-accent flex-shrink-0" />
                <span className="truncate">{fanPageHref}</span>
              </div>
              <a
                href={fanPageHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full border border-border rounded-[11px] px-[15px] py-2 text-[14px] font-semibold text-foreground hover:bg-muted transition-colors">
                <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
                View live page
              </a>
            </div>
          }

          {/* Report a bug / idea — tenant and kiosk portals. Opens the modal
              instantly; the optional screenshot is captured from inside it. */}
          {isPortal &&
            <button
              type="button"
              onClick={handleOpenFeedback}
              className="inline-flex items-center justify-center gap-2 w-full border border-border rounded-[11px] px-[15px] py-2 text-[14px] font-semibold text-foreground hover:bg-muted transition-colors">
              <BugIcon className="w-4 h-4" strokeWidth={1.8} />
              Report a bug / idea
            </button>
          }

          {/* Account — mockup .acct */}
          <div className="flex items-center gap-2.5 px-1.5 py-2">
            <div className="w-[30px] h-[30px] rounded-full bg-muted text-foreground/80 flex items-center justify-center font-bold text-xs flex-shrink-0">
              {accountInitials(user?.email)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground truncate">{user?.email}</div>
              <div className="text-sm font-medium text-muted-foreground">
                {isInternalAdmin ? 'IDSeat admin' : (ROLE_LABEL[activeWorkspace?.role ?? ''] ?? 'Owner')}
              </div>
            </div>
            <button
              onClick={handleSignOut}
              aria-label="Sign Out"
              className="text-muted-foreground hover:text-destructive hover:bg-muted rounded-md transition-colors p-1">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      {/* `relative` makes this scroll container the containing block for any
          absolutely positioned descendant (e.g. shadcn's sr-only labels). Without
          it they anchor to the document instead, escape the shell's one-viewport
          box, and stretch the page: a second, document-level scrollbar that
          scrolls the whole shell out of view. */}
      <main className="relative flex-1 min-w-0 h-full overflow-y-auto flex flex-col bg-surface">
        {/* IDSeat inside someone else's portal: who, and the way back. */}
        {isPortal && <ViewAsBanner />}
        {/* Top bar — mockup .topbar */}
        <header className="h-[62px] flex-shrink-0 bg-card border-b border-border flex items-center justify-between px-4 lg:px-[26px] sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 text-foreground">
              <Menu className="w-5 h-5" />
            </button>
            {/* Breadcrumb — mockup .crumb */}
            <div className="text-[15px] font-semibold text-muted-foreground truncate">
              {isPortal ? (
                <>
                  <span>{portalName}&nbsp;&nbsp;/&nbsp;&nbsp;</span>
                  <span className="font-bold text-foreground">{title || 'Admin'}</span>
                </>
              ) : breadcrumbs && breadcrumbs.length > 0 ? (
                breadcrumbs.map((crumb, index) => {
                  const last = index === breadcrumbs.length - 1;
                  return (
                    <span key={index}>
                      {index > 0 && <span className="mx-2 opacity-50">/</span>}
                      {last ? (
                        <span className="font-bold text-foreground">{crumb.label}</span>
                      ) : crumb.href ? (
                        <Link to={crumb.href} className="hover:text-foreground transition-colors">
                          {crumb.label}
                        </Link>
                      ) : (
                        <span>{crumb.label}</span>
                      )}
                    </span>
                  );
                })
              ) : (
                <span className="font-bold text-foreground">{title || 'Admin'}</span>
              )}
            </div>
          </div>

          {/* Actions — mockup .btnrow */}
          <div className="flex items-center gap-2.5">
            {/* Visible on every width — it was `hidden sm:flex`, which left a
                multi-club admin on a phone unable to see or change the club (main, PR #153). */}
            {isPortal && workspaces.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 rounded-[11px] min-w-0 max-w-[50vw] sm:max-w-none">
                    <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-semibold truncate">{activeWorkspace?.name ?? portalName}</span>
                    <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {workspaces.map((w) => {
                    const active = activeWorkspace?.kind === w.kind && activeWorkspace.id === w.id;
                    return (
                      <DropdownMenuItem
                        key={`${w.kind}:${w.id}`}
                        className={cn(
                          "flex items-center gap-2 cursor-pointer",
                          active && "bg-accent/10 text-accent font-medium"
                        )}
                        onClick={() => {
                          // Each kind has its own portal, so switching also moves there.
                          switchWorkspace(w.kind, w.id);
                          navigate(workspaceHome(w.kind));
                        }}
                      >
                        <Building2 className="w-4 h-4" />
                        <span className="truncate">{w.name}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">{workspaceKindLabel(w.kind)}</span>
                        {active && <div className="w-1.5 h-1.5 rounded-full bg-accent" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isInternalAdmin && (
              <button
                onClick={() => setPaletteOpen(true)}
                className="hidden sm:flex items-center gap-2 w-64 rounded-[11px] border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors">
                <Search className="w-4 h-4 flex-shrink-0" strokeWidth={1.8} />
                <span className="flex-1 text-left truncate">Jump to a club, venue or fixture…</span>
                <kbd className="ml-auto inline-flex items-center rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground/70">
                  ⌘K
                </kbd>
              </button>
            )}
            <ThemeToggle />
          </div>
        </header>

        {isInternalAdmin && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
        {isPortal && (
          <FeedbackModal
            isOpen={feedbackOpen}
            initialScreenshot={feedbackShot}
            onClose={() => {
              setFeedbackOpen(false);
              setFeedbackShot(null);
            }}
          />
        )}

        {/* Quiet translation status, replaces the old per-save toasts */}
        {isClub && <TranslationActivityLine />}

        {/* Page content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * Public admin layout. Historically every page wrapped itself in
 * `<AdminLayout title breadcrumbs>` and got the full shell. Now the shell lives
 * once in `AdminLayoutRoute` (a persistent layout route), so this component
 * guards against rendering a second shell: when it detects it's already inside
 * an `AdminLayoutRoute` (via `useShellRendered()`), it renders only its children
 * and drops the title/breadcrumbs (the layout route owns those, from the route
 * `handle`). Used standalone (no layout route above), it renders the full shell
 * exactly as before — backward-compatible for pages not yet migrated.
 */
export function AdminLayout({ children, title, breadcrumbs }: AdminLayoutProps) {
  const shellRendered = useShellRendered();
  if (shellRendered) return <>{children}</>;
  return (
    <AdminShell title={title} breadcrumbs={breadcrumbs}>
      {children}
    </AdminShell>
  );
}

/**
 * Persistent layout route. Renders the admin shell once and swaps only the
 * `<Outlet/>` content on navigation, so the sidebar/topbar, ⌘K/body-font
 * effects and the orders realtime channel survive route changes. Title and
 * breadcrumbs come from the deepest matched route's `handle` (see App.tsx),
 * with an optional per-route override via `usePageTitle()` for dynamic titles.
 */
export function AdminLayoutRoute() {
  const location = useLocation();
  const matches = useMatches();
  // Path-scoped override so a stale dynamic title never leaks across routes:
  // it only applies while the pathname it was set for is still active.
  const [override, setOverride] = useState<{ path: string; title: string } | null>(null);

  const setTitle = useCallback(
    (title: string | null) => {
      setOverride(title == null ? null : { path: location.pathname, title });
    },
    [location.pathname],
  );

  // Deepest matched route that carries a title in its handle.
  const handle = [...matches]
    .reverse()
    .map((m) => m.handle as AdminRouteHandle | undefined)
    .find((h) => !!h?.title);

  const titleOverride = override && override.path === location.pathname ? override.title : null;
  const title = titleOverride ?? handle?.title ?? '';
  const breadcrumbs = handle?.breadcrumbs;

  // shellRendered:true tells any nested (unconverted) <AdminLayout> in the
  // Outlet to render children only, so it doesn't double the shell.
  const ctxValue = useMemo<PageTitleContextValue>(
    () => ({ setTitle, shellRendered: true }),
    [setTitle],
  );

  return (
    <AdminLayout title={title} breadcrumbs={breadcrumbs}>
      <PageTitleContext.Provider value={ctxValue}>
        {/* key re-triggers the entrance animation on each route change */}
        <div key={location.pathname} className="motion-safe:animate-fade-in-up">
          <Suspense fallback={<ContentSkeleton />}>
            <InternalAdminMfaGate>
              <Outlet />
            </InternalAdminMfaGate>
          </Suspense>
        </div>
      </PageTitleContext.Provider>
    </AdminLayout>
  );
}
